<template>
  <div class="editor-lineas-factura">
    <div class="row g-2 mb-1 small fw-semibold text-muted d-none d-md-flex">
      <div class="col-md-5">Descripción</div>
      <div class="col-md-2">Cant.</div>
      <div class="col-md-2">P. Unit (Q)</div>
      <div class="col-md-2 text-end">Subtotal</div>
    </div>

    <div v-for="(linea, index) in lineas" :key="index" class="row g-2 mb-2 align-items-center">
      <div class="col-12 col-md-5">
        <input
          type="text"
          v-model="linea.descripcion"
          class="form-control form-control-sm"
          :placeholder="placeholderDescripcion"
          maxlength="500"
        />
      </div>
      <div class="col-4 col-md-2">
        <input
          type="number"
          v-model.number="linea.cantidad"
          min="1"
          step="1"
          class="form-control form-control-sm"
          placeholder="Cant."
        />
      </div>
      <div class="col-4 col-md-2">
        <input
          type="number"
          v-model.number="linea.precio_unitario"
          min="0.01"
          step="0.01"
          class="form-control form-control-sm"
          placeholder="P. Unit"
        />
      </div>
      <div class="col-3 col-md-2 text-end small fw-semibold">
        Q{{ subtotal(linea).toFixed(2) }}
      </div>
      <div class="col-1 col-md-1">
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          @click="quitarLinea(index)"
          :disabled="lineas.length <= 1"
          title="Quitar línea"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <button
      type="button"
      class="btn btn-outline-success btn-sm mb-2"
      @click="agregarLinea"
      :disabled="lineas.length >= maxLineas"
    >
      <i class="bi bi-plus-lg me-1"></i>Agregar línea
    </button>

    <div class="alert py-2 mb-0 small" :class="claseResumen">
      <div class="d-flex justify-content-between">
        <span>Total a facturar{{ etiqueta ? ` (${etiqueta})` : "" }}:</span>
        <strong>Q{{ totalObjetivo.toFixed(2) }}</strong>
      </div>
      <div class="d-flex justify-content-between">
        <span>Suma de las líneas:</span>
        <strong>Q{{ suma.toFixed(2) }}</strong>
      </div>
      <div v-if="!cuadra && diferencia > 0" class="d-flex justify-content-between border-top pt-1 mt-1">
        <span>Faltan:</span>
        <strong>Q{{ diferencia.toFixed(2) }}</strong>
      </div>
      <div v-else-if="!cuadra" class="d-flex justify-content-between border-top pt-1 mt-1">
        <span>Sobran:</span>
        <strong>Q{{ (-diferencia).toFixed(2) }}</strong>
      </div>
    </div>
    <div v-if="mensajeError" class="small text-danger mt-2">
      <i class="bi bi-exclamation-triangle me-1"></i>{{ mensajeError }}
    </div>
  </div>
</template>

<script>
/**
 * Líneas de la factura detallada: descripción + cantidad + precio unitario
 * (ej. "Desayunos" · 11 · Q50.00 = Q550.00). Las líneas solo reparten el
 * total del grupo (hospedaje o restaurante): su suma debe cuadrar al centavo,
 * igual que valida el backend (LineasDetalleFactura.php).
 *
 * Se monta con v-if al activar el detalle: arranca con una línea que ya cuadra
 * (cantidad 1 × total) y el cajero la ajusta.
 */
const MAX_LINEAS = 20;

const redondear = (valor) => Math.round((Number(valor) || 0) * 100) / 100;

export default {
  name: "EditorLineasFactura",
  props: {
    totalObjetivo: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    descripcionSugerida: {
      type: String,
      default: "",
    },
    etiqueta: {
      type: String,
      default: "",
    },
    placeholderDescripcion: {
      type: String,
      default: "Ej: Desayunos",
    },
  },
  emits: ["update:modelValue", "update:valido"],
  data() {
    return {
      maxLineas: MAX_LINEAS,
      lineas:
        this.modelValue && this.modelValue.length > 0
          ? this.modelValue.map((l) => ({ ...l }))
          : [
              {
                descripcion: this.descripcionSugerida,
                cantidad: 1,
                precio_unitario: redondear(this.totalObjetivo),
              },
            ],
    };
  },
  computed: {
    suma() {
      return redondear(this.lineas.reduce((acc, l) => acc + this.subtotal(l), 0));
    },
    diferencia() {
      return redondear(this.totalObjetivo - this.suma);
    },
    // Las líneas suman el total al centavo, igual que valida el backend
    cuadra() {
      return Math.abs(this.diferencia) <= 0.004;
    },
    mensajeError() {
      for (const l of this.lineas) {
        if (!String(l.descripcion || "").trim()) {
          return "Cada línea necesita una descripción.";
        }
        if (!Number.isInteger(l.cantidad) || l.cantidad < 1) {
          return "La cantidad debe ser un número entero mayor a 0.";
        }
        if (!(redondear(l.precio_unitario) > 0)) {
          return "El precio unitario debe ser mayor a 0.";
        }
      }
      if (!this.cuadra) {
        return "La suma de las líneas debe ser igual al total a facturar.";
      }
      return "";
    },
    esValido() {
      return !this.mensajeError;
    },
    claseResumen() {
      return this.cuadra ? "alert-success" : "alert-danger";
    },
  },
  watch: {
    // Si el total cambia (otro monto u otra selección) y la línea inicial sigue
    // intacta (1 × total anterior), se ajusta sola; líneas editadas no se tocan.
    totalObjetivo(nuevo, anterior) {
      const [unica] = this.lineas;
      if (
        this.lineas.length === 1 &&
        unica.cantidad === 1 &&
        redondear(unica.precio_unitario) === redondear(anterior)
      ) {
        unica.precio_unitario = redondear(nuevo);
      }
    },
    lineas: {
      deep: true,
      immediate: true,
      handler(val) {
        this.$emit(
          "update:modelValue",
          val.map((l) => ({
            descripcion: String(l.descripcion || "").trim(),
            cantidad: l.cantidad,
            precio_unitario: redondear(l.precio_unitario),
          })),
        );
      },
    },
    esValido: {
      immediate: true,
      handler(val) {
        this.$emit("update:valido", val);
      },
    },
  },
  methods: {
    subtotal(linea) {
      return redondear((Number(linea.cantidad) || 0) * redondear(linea.precio_unitario));
    },
    agregarLinea() {
      if (this.lineas.length >= MAX_LINEAS) return;
      this.lineas.push({
        descripcion: "",
        cantidad: 1,
        precio_unitario: this.diferencia > 0 ? this.diferencia : null,
      });
    },
    quitarLinea(index) {
      if (this.lineas.length <= 1) return;
      this.lineas.splice(index, 1);
    },
  },
};
</script>
